'use client'

import {
  FileText,
  GitPullRequest,
  CheckCircle,
  RefreshCw,
  TrendingDown,
  Target,
  Zap,
  Building2,
  UserCheck,
  User,
  Check,
  AlertTriangle,
  Info,
  Bot,
  Lock,
  BarChart3,
} from 'lucide-react'

/**
 * AI Technical Debt Crisis Diagrams
 * Visual components for the vibe coding technical debt blog post
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

// Developer Trust Erosion Timeline
export function TrustErosionDiagram() {
  const dataPoints = [
    { period: '2024', trust: 43, usage: 76, y: 57 },
    { period: 'Early 2025', trust: 33, usage: 80, y: 67 },
    { period: 'Mid 2025', trust: 29, usage: 84, y: 71 },
    { period: 'Late 2025', trust: 54, usage: 84, y: 46 }, // 46% don't trust = 54% trust (rebounded)
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          THE TRUST PARADOX
        </h3>
        <p className='text-sm text-muted-foreground'>
          Usage increases while trust plummets — developers use tools they don't
          believe in
        </p>
      </div>

      <div className='flex flex-col items-center gap-6'>
        {/* Chart visualization */}
        <div className='w-full max-w-lg'>
          {/* Y-axis labels */}
          <div className='flex items-end gap-4'>
            <div className='flex flex-col justify-between h-40 text-right pr-2'>
              <span className='text-[10px] text-muted-foreground'>100%</span>
              <span className='text-[10px] text-muted-foreground'>75%</span>
              <span className='text-[10px] text-muted-foreground'>50%</span>
              <span className='text-[10px] text-muted-foreground'>25%</span>
              <span className='text-[10px] text-muted-foreground'>0%</span>
            </div>

            {/* Chart area */}
            <div className='flex-1 relative h-40 border-l border-b border-border/60'>
              {/* Grid lines */}
              {[0, 1, 2, 3].map(i => (
                <div
                  key={i}
                  className='absolute w-full border-t border-border/30'
                  style={{ bottom: `${(i + 1) * 25}%` }}
                />
              ))}

              {/* Usage line (green, high) */}
              <svg
                className='absolute inset-0 w-full h-full'
                preserveAspectRatio='none'
              >
                <polyline
                  points='0,24 33,20 66,16 100,16'
                  fill='none'
                  stroke='#22c55e'
                  strokeWidth='2'
                  strokeDasharray='4,2'
                  vectorEffect='non-scaling-stroke'
                  style={{ transform: 'scaleX(1)' }}
                />
              </svg>

              {/* Trust line (red, declining then rebounding) */}
              <svg
                className='absolute inset-0 w-full h-full'
                preserveAspectRatio='none'
              >
                <polyline
                  points='0,57 33,67 66,71 100,46'
                  fill='none'
                  stroke='#ef4444'
                  strokeWidth='3'
                  vectorEffect='non-scaling-stroke'
                />
              </svg>

              {/* Data points */}
              {dataPoints.map((point, idx) => (
                <div
                  key={idx}
                  className='absolute flex flex-col items-center'
                  style={{
                    left: `${idx * 33.33}%`,
                    bottom: `${100 - point.y}%`,
                    transform: 'translateX(-50%)',
                  }}
                >
                  <div className='w-3 h-3 rounded-full bg-red-500 border-2 border-white' />
                  <span className='text-[10px] font-bold text-red-500 mt-1'>
                    {point.trust}%
                  </span>
                </div>
              ))}

              {/* X-axis labels */}
              <div className='absolute -bottom-6 left-0 right-0 flex justify-between'>
                {dataPoints.map((point, idx) => (
                  <span
                    key={idx}
                    className='text-[10px] text-muted-foreground text-center'
                    style={{ width: '25%' }}
                  >
                    {point.period}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className='flex items-center justify-center gap-6 mt-10'>
            <div className='flex items-center gap-2'>
              <div className='w-4 h-0.5 bg-red-500' />
              <span className='text-xs text-muted-foreground'>
                Trust in AI accuracy
              </span>
            </div>
            <div className='flex items-center gap-2'>
              <div
                className='w-4 h-0.5 bg-green-500 border-dashed'
                style={{ borderStyle: 'dashed' }}
              />
              <span className='text-xs text-muted-foreground'>
                AI tool usage
              </span>
            </div>
          </div>
        </div>

        {/* Key stat callouts */}
        <div className='grid grid-cols-2 gap-3 w-full max-w-md'>
          <div className='p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-center'>
            <p className='text-2xl md:text-3xl font-bold text-red-500'>
              43% → 29%
            </p>
            <p className='text-[10px] md:text-xs text-muted-foreground'>
              Trust dropped in 18 months
            </p>
          </div>
          <div className='p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-center'>
            <p className='text-2xl md:text-3xl font-bold text-green-500'>84%</p>
            <p className='text-[10px] md:text-xs text-muted-foreground'>
              Developers using AI tools
            </p>
          </div>
        </div>

        {/* Key insight */}
        <div className='mt-2 px-4 py-2 bg-red-500/10 rounded-lg border border-red-500/30'>
          <p className='text-xs md:text-sm text-center text-foreground'>
            <span className='font-semibold'>Cognitive dissonance:</span>{' '}
            Developers use tools they increasingly distrust because
            organizational pressure mandates adoption.
          </p>
        </div>
      </div>
    </div>
  )
}

// Code Quality Metrics Comparison (2021 vs 2024)
export function CodeQualityMetricsDiagram() {
  const metrics = [
    {
      name: 'Code Duplication',
      before: 8.3,
      after: 12.3,
      change: '+48%',
      bad: true,
      color: '#ef4444',
    },
    {
      name: 'Refactoring Activity',
      before: 25,
      after: 10,
      change: '-60%',
      bad: true,
      color: '#ef4444',
    },
    {
      name: 'Code Churn Rate',
      before: 100,
      after: 141,
      change: '+41%',
      bad: true,
      color: '#ef4444',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          CODE QUALITY DEGRADATION
        </h3>
        <p className='text-sm text-muted-foreground'>
          GitClear analysis of 153 million lines of code (2021 vs 2024)
        </p>
      </div>

      <div className='flex flex-col items-center gap-6'>
        {/* Horizontal bar chart */}
        <div className='w-full max-w-lg space-y-4'>
          {metrics.map((metric, idx) => (
            <div key={idx} className='space-y-2'>
              <div className='flex items-center justify-between'>
                <span className='text-xs md:text-sm font-medium text-foreground'>
                  {metric.name}
                </span>
                <span className='text-xs md:text-sm font-bold text-red-500'>
                  {metric.change}
                </span>
              </div>

              <div className='relative h-8 bg-muted rounded-lg overflow-hidden'>
                {/* 2021 bar */}
                <div
                  className='absolute top-0 left-0 h-1/2 bg-[#008dcc] rounded-tl-lg flex items-center justify-end pr-2'
                  style={{ width: `${(metric.before / 150) * 100}%` }}
                >
                  <span className='text-[10px] font-bold text-white'>
                    {metric.before}%
                  </span>
                </div>
                {/* 2024 bar */}
                <div
                  className='absolute bottom-0 left-0 h-1/2 bg-red-500 rounded-bl-lg flex items-center justify-end pr-2'
                  style={{ width: `${(metric.after / 150) * 100}%` }}
                >
                  <span className='text-[10px] font-bold text-white'>
                    {metric.after}%
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className='flex items-center justify-center gap-6'>
          <div className='flex items-center gap-2'>
            <div className='w-4 h-3 rounded bg-[#008dcc]' />
            <span className='text-xs text-muted-foreground'>2021 (Pre-AI)</span>
          </div>
          <div className='flex items-center gap-2'>
            <div className='w-4 h-3 rounded bg-red-500' />
            <span className='text-xs text-muted-foreground'>
              2024 (Post-AI)
            </span>
          </div>
        </div>

        {/* Problem indicators */}
        <div className='grid grid-cols-3 gap-2 w-full max-w-lg'>
          <div className='p-2 rounded-lg bg-muted text-center'>
            <div className='flex items-center justify-center gap-1 mb-1'>
              <svg
                className='w-4 h-4 text-red-500'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
              >
                <path d='M7 7h10v10' strokeLinecap='round' />
                <path d='M7 17L17 7' strokeLinecap='round' />
              </svg>
              <span className='text-[10px] font-bold text-red-500'>
                DUPLICATION
              </span>
            </div>
            <p className='text-[9px] text-muted-foreground'>
              Copy-paste patterns increase maintenance
            </p>
          </div>
          <div className='p-2 rounded-lg bg-muted text-center'>
            <div className='flex items-center justify-center gap-1 mb-1'>
              <svg
                className='w-4 h-4 text-red-500'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
              >
                <path d='M17 17l-10-10' strokeLinecap='round' />
                <path d='M7 17V7h10' strokeLinecap='round' />
              </svg>
              <span className='text-[10px] font-bold text-red-500'>
                REFACTORING
              </span>
            </div>
            <p className='text-[9px] text-muted-foreground'>
              Architecture improvements collapse
            </p>
          </div>
          <div className='p-2 rounded-lg bg-muted text-center'>
            <div className='flex items-center justify-center gap-1 mb-1'>
              <svg
                className='w-4 h-4 text-red-500'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
              >
                <circle cx='12' cy='12' r='10' />
                <path d='M12 6v6l4 2' strokeLinecap='round' />
              </svg>
              <span className='text-[10px] font-bold text-red-500'>CHURN</span>
            </div>
            <p className='text-[9px] text-muted-foreground'>
              Code changed within 2 weeks of creation
            </p>
          </div>
        </div>

        {/* Key insight */}
        <div className='mt-2 px-4 py-2 bg-[#008dcc]/10 rounded-lg border border-[#008dcc]/30'>
          <p className='text-xs md:text-sm text-center text-foreground'>
            <span className='font-semibold'>The productivity trap:</span> Teams
            spend more time fixing recent AI-generated mistakes than building
            new features.
          </p>
        </div>
      </div>
    </div>
  )
}

// Security Vulnerability Spike Diagram
export function SecuritySpikeDiagram() {
  const categories = [
    { name: 'Insecure Patterns', dec: 250, june: 2500, color: '#ef4444' },
    { name: 'Exposed Secrets', dec: 250, june: 2500, color: '#f97316' },
    { name: 'Dependency Issues', dec: 250, june: 2500, color: '#FDC304' },
    { name: 'Cloud Misconfig', dec: 250, june: 2000, color: '#F90B8A' },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          10x SECURITY VULNERABILITY SPIKE
        </h3>
        <p className='text-sm text-muted-foreground'>
          Fortune 50 enterprises: Dec 2024 → June 2025 (Apiiro research)
        </p>
      </div>

      <div className='flex flex-col items-center gap-6'>
        {/* Before/After comparison */}
        <div className='flex items-end justify-center gap-8 md:gap-16 w-full'>
          {/* December 2024 */}
          <div className='flex flex-col items-center'>
            <div className='relative w-16 md:w-20 flex flex-col items-center'>
              <div className='w-full h-12 bg-gradient-to-t from-[#008dcc] to-[#008dcc]/60 rounded-t-lg flex items-center justify-center'>
                <span className='text-xs md:text-sm font-bold text-white'>
                  ~1K
                </span>
              </div>
            </div>
            <span className='text-xs text-muted-foreground mt-2'>Dec 2024</span>
            <span className='text-[10px] text-muted-foreground'>
              findings/month
            </span>
          </div>

          {/* Arrow with 10x label */}
          <div className='flex flex-col items-center gap-1 pb-8'>
            <div className='px-3 py-1 rounded-full bg-red-500 text-white'>
              <span className='text-sm md:text-base font-bold'>10x</span>
            </div>
            <svg
              className='w-16 h-4 text-red-500'
              viewBox='0 0 64 16'
              fill='none'
            >
              <path
                d='M0 8h56M48 2l8 6-8 6'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
            <span className='text-[10px] text-red-500'>6 months</span>
          </div>

          {/* June 2025 */}
          <div className='flex flex-col items-center'>
            <div className='relative w-16 md:w-20 flex flex-col items-center'>
              {/* Stacked vulnerability categories */}
              <div className='w-full flex flex-col-reverse'>
                {categories.map((cat, idx) => (
                  <div
                    key={idx}
                    className='w-full flex items-center justify-center'
                    style={{
                      height: `${cat.june / 100}px`,
                      backgroundColor: cat.color,
                      borderTopLeftRadius:
                        idx === categories.length - 1 ? '8px' : 0,
                      borderTopRightRadius:
                        idx === categories.length - 1 ? '8px' : 0,
                    }}
                  />
                ))}
              </div>
              <div className='absolute top-2 left-1/2 -translate-x-1/2'>
                <span className='text-xs md:text-sm font-bold text-white'>
                  10K+
                </span>
              </div>
            </div>
            <span className='text-xs text-muted-foreground mt-2'>
              June 2025
            </span>
            <span className='text-[10px] text-muted-foreground'>
              findings/month
            </span>
          </div>
        </div>

        {/* Category legend */}
        <div className='grid grid-cols-2 md:grid-cols-4 gap-2 w-full max-w-lg'>
          {categories.map((cat, idx) => (
            <div key={idx} className='flex items-center gap-2'>
              <div
                className='w-3 h-3 rounded'
                style={{ backgroundColor: cat.color }}
              />
              <span className='text-[10px] md:text-xs text-muted-foreground'>
                {cat.name}
              </span>
            </div>
          ))}
        </div>

        {/* Vulnerability stats */}
        <div className='grid grid-cols-3 gap-3 w-full max-w-lg'>
          <div className='p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-center'>
            <p className='text-xl md:text-2xl font-bold text-red-500'>45%</p>
            <p className='text-[10px] text-muted-foreground'>
              AI code with vulnerabilities
            </p>
          </div>
          <div className='p-3 rounded-lg bg-orange-500/10 border border-orange-500/30 text-center'>
            <p className='text-xl md:text-2xl font-bold text-orange-500'>2x</p>
            <p className='text-[10px] text-muted-foreground'>
              More credential exposure
            </p>
          </div>
          <div className='p-3 rounded-lg bg-[#F90B8A]/10 border border-[#F90B8A]/30 text-center'>
            <p className='text-xl md:text-2xl font-bold text-[#F90B8A]'>41%</p>
            <p className='text-[10px] text-muted-foreground'>
              Of all code now AI-generated
            </p>
          </div>
        </div>

        {/* Key insight */}
        <div className='mt-2 px-4 py-2 bg-red-500/10 rounded-lg border border-red-500/30'>
          <p className='text-xs md:text-sm text-center text-foreground'>
            <span className='font-semibold'>
              Systematic vulnerability generation at scale:
            </span>{' '}
            Unlike traditional debt that accumulates gradually, AI creates
            vulnerable code exponentially.
          </p>
        </div>
      </div>
    </div>
  )
}

// Vibe Coding vs Traditional Development Tradeoffs
export function VibeCodingTradeoffsDiagram() {
  const dimensions = [
    {
      name: 'Speed',
      vibe: 90,
      traditional: 30,
      vibeLabel: '2-4 weeks',
      tradLabel: '4-6 months',
    },
    {
      name: 'Code Quality',
      vibe: 35,
      traditional: 85,
      vibeLabel: '80/20 wrong',
      tradLabel: 'Reviewed',
    },
    {
      name: 'Security',
      vibe: 25,
      traditional: 80,
      vibeLabel: '45% vulnerable',
      tradLabel: 'Audited',
    },
    {
      name: 'Maintainability',
      vibe: 30,
      traditional: 85,
      vibeLabel: 'Unknown',
      tradLabel: 'Proven',
    },
    {
      name: 'Long-term Cost',
      vibe: 40,
      traditional: 75,
      vibeLabel: '+cleanup',
      tradLabel: 'Predictable',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          THE SPEED vs QUALITY TRADEOFF
        </h3>
        <p className='text-sm text-muted-foreground'>
          Vibe coding wins on speed, loses on everything else
        </p>
      </div>

      <div className='flex flex-col items-center gap-6'>
        {/* Radar-style comparison */}
        <div className='w-full max-w-lg space-y-3'>
          {dimensions.map((dim, idx) => (
            <div key={idx} className='space-y-1'>
              <div className='flex items-center justify-between text-xs'>
                <span className='font-medium text-foreground w-28'>
                  {dim.name}
                </span>
                <span className='text-[#FDC304] text-[10px]'>
                  {dim.vibeLabel}
                </span>
                <span className='text-[#008dcc] text-[10px]'>
                  {dim.tradLabel}
                </span>
              </div>
              <div className='relative h-6 bg-muted rounded-full overflow-hidden'>
                {/* Traditional bar (from right) */}
                <div
                  className='absolute top-0 right-0 h-full bg-[#008dcc]/30 flex items-center justify-start pl-2'
                  style={{ width: `${dim.traditional}%` }}
                >
                  {dim.traditional > 50 && (
                    <span className='text-[10px] font-medium text-[#008dcc]'>
                      {dim.traditional}%
                    </span>
                  )}
                </div>
                {/* Vibe bar (from left) */}
                <div
                  className='absolute top-0 left-0 h-full bg-[#FDC304]/80 flex items-center justify-end pr-2'
                  style={{ width: `${dim.vibe}%` }}
                >
                  {dim.vibe > 30 && (
                    <span className='text-[10px] font-medium text-black'>
                      {dim.vibe}%
                    </span>
                  )}
                </div>
                {/* Center line */}
                <div className='absolute top-0 left-1/2 w-0.5 h-full bg-foreground/20' />
              </div>
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className='flex items-center justify-center gap-6'>
          <div className='flex items-center gap-2'>
            <div className='w-4 h-3 rounded bg-[#FDC304]' />
            <span className='text-xs text-muted-foreground'>Vibe Coding</span>
          </div>
          <div className='flex items-center gap-2'>
            <div className='w-4 h-3 rounded bg-[#008dcc]/50' />
            <span className='text-xs text-muted-foreground'>
              Traditional Dev
            </span>
          </div>
        </div>

        {/* Cost comparison */}
        <div className='grid grid-cols-2 gap-4 w-full max-w-md'>
          <div className='p-4 rounded-xl border-2 border-[#FDC304] bg-[#FDC304]/5'>
            <div className='flex items-center gap-2 mb-2'>
              <Zap className='w-5 h-5 text-[#FDC304]' />
              <span className='font-bold text-sm text-foreground'>
                Vibe Coding
              </span>
            </div>
            <p className='text-xl font-bold text-[#FDC304]'>£15K-40K</p>
            <p className='text-[10px] text-muted-foreground'>
              2-4 weeks to MVP
            </p>
            <p className='text-[10px] text-red-500 mt-1'>
              + unknown cleanup costs
            </p>
          </div>
          <div className='p-4 rounded-xl border-2 border-[#008dcc] bg-[#008dcc]/5'>
            <div className='flex items-center gap-2 mb-2'>
              <Building2 className='w-5 h-5 text-[#008dcc]' />
              <span className='font-bold text-sm text-foreground'>
                Traditional
              </span>
            </div>
            <p className='text-xl font-bold text-[#008dcc]'>£50K-200K+</p>
            <p className='text-[10px] text-muted-foreground'>
              4-6 months to MVP
            </p>
            <p className='text-[10px] text-green-500 mt-1'>
              Predictable maintenance
            </p>
          </div>
        </div>

        {/* Key insight */}
        <div className='mt-2 px-4 py-2 bg-[#FDC304]/10 rounded-lg border border-[#FDC304]/30'>
          <p className='text-xs md:text-sm text-center text-foreground'>
            <span className='font-semibold'>Critical limitation:</span> Vibe
            coding is only 18 months old. No organization has implemented a 5-10
            year maintenance plan.
          </p>
        </div>
      </div>
    </div>
  )
}

// The Productivity Paradox Diagram
export function ProductivityParadoxDiagram() {
  const gains = [
    { metric: 'Weekly Commits', change: '+13.5%', Icon: FileText },
    { metric: 'Pull Requests', change: '+10.6%', Icon: GitPullRequest },
    { metric: 'Successful Builds', change: '+84%', Icon: CheckCircle },
  ]

  const losses = [
    { metric: 'Code Churn', change: '+41%', Icon: RefreshCw },
    { metric: 'Delivery Stability', change: '-7.2%', Icon: TrendingDown },
    { metric: '"Almost Right" Code', change: '66%', Icon: Target },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          THE PRODUCTIVITY PARADOX
        </h3>
        <p className='text-sm text-muted-foreground'>
          More output, less quality — 95% report gains, but at what cost?
        </p>
      </div>

      <div className='flex flex-col items-center gap-6'>
        {/* Two columns comparison */}
        <div className='grid grid-cols-2 gap-4 w-full max-w-lg'>
          {/* Gains column */}
          <div className='p-4 rounded-xl bg-green-500/10 border border-green-500/30'>
            <div className='flex items-center gap-2 mb-3'>
              <div className='w-8 h-8 rounded-full bg-green-500 flex items-center justify-center'>
                <TrendingDown className='w-4 h-4 text-white rotate-180' />
              </div>
              <span className='font-bold text-sm text-green-600 dark:text-green-400'>
                Surface Gains
              </span>
            </div>
            <div className='space-y-2'>
              {gains.map((item, idx) => (
                <div
                  key={idx}
                  className='flex items-center justify-between p-2 bg-green-500/10 rounded-lg'
                >
                  <div className='flex items-center gap-2'>
                    <item.Icon className='w-4 h-4 text-green-600 dark:text-green-400' />
                    <span className='text-[10px] md:text-xs text-foreground'>
                      {item.metric}
                    </span>
                  </div>
                  <span className='text-xs font-bold text-green-600 dark:text-green-400'>
                    {item.change}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Losses column */}
          <div className='p-4 rounded-xl bg-red-500/10 border border-red-500/30'>
            <div className='flex items-center gap-2 mb-3'>
              <div className='w-8 h-8 rounded-full bg-red-500 flex items-center justify-center'>
                <TrendingDown className='w-4 h-4 text-white' />
              </div>
              <span className='font-bold text-sm text-red-600 dark:text-red-400'>
                Hidden Costs
              </span>
            </div>
            <div className='space-y-2'>
              {losses.map((item, idx) => (
                <div
                  key={idx}
                  className='flex items-center justify-between p-2 bg-red-500/10 rounded-lg'
                >
                  <div className='flex items-center gap-2'>
                    <item.Icon className='w-4 h-4 text-red-600 dark:text-red-400' />
                    <span className='text-[10px] md:text-xs text-foreground'>
                      {item.metric}
                    </span>
                  </div>
                  <span className='text-xs font-bold text-red-600 dark:text-red-400'>
                    {item.change}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* The "almost right" problem */}
        <div className='w-full max-w-lg p-4 rounded-xl border-2 border-[#FDC304] bg-[#FDC304]/5'>
          <div className='flex items-center gap-3'>
            <div className='w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-red-500 flex items-center justify-center relative'>
              <div className='absolute inset-2 rounded-full bg-background flex items-center justify-center'>
                <span className='text-xl font-bold text-foreground'>80%</span>
              </div>
            </div>
            <div className='flex-1'>
              <p className='text-sm font-bold text-foreground mb-1'>
                The "Almost Right" Problem
              </p>
              <p className='text-xs text-muted-foreground'>
                AI code that appears to work but is "almost, but not quite,
                right" — creating subtle bugs that are time-intensive to detect
                and resolve.
              </p>
            </div>
          </div>
        </div>

        {/* Stack Overflow stat */}
        <div className='flex items-center gap-3 p-3 rounded-lg bg-muted'>
          <div className='w-10 h-10 rounded bg-orange-500 flex items-center justify-center'>
            <span className='text-white font-bold text-xs'>SO</span>
          </div>
          <div>
            <p className='text-sm font-semibold text-foreground'>
              1/3 of Stack Overflow visits
            </p>
            <p className='text-[10px] text-muted-foreground'>
              Now stem from issues related to AI-generated code
            </p>
          </div>
        </div>

        {/* Key insight */}
        <div className='mt-2 px-4 py-2 bg-[#008dcc]/10 rounded-lg border border-[#008dcc]/30'>
          <p className='text-xs md:text-sm text-center text-foreground'>
            <span className='font-semibold'>The productivity tax:</span> If
            correction time exceeds generation time, productivity becomes
            negative despite superficial metrics.
          </p>
        </div>
      </div>
    </div>
  )
}

// Junior Developer Squeeze Diagram
export function JuniorDeveloperSqueezeDiagram() {
  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          THE HOLLOWED-OUT CAREER LADDER
        </h3>
        <p className='text-sm text-muted-foreground'>
          54% of engineering leaders plan to hire fewer juniors due to AI
        </p>
      </div>

      <div className='flex flex-col items-center gap-6'>
        {/* Career ladder visualization */}
        <div className='relative w-full max-w-md'>
          {/* The ladder */}
          <div className='flex flex-col items-center gap-2'>
            {/* Senior level */}
            <div className='w-full p-4 rounded-xl bg-[#008dcc]/20 border-2 border-[#008dcc] flex items-center justify-between'>
              <div className='flex items-center gap-3'>
                <div className='w-10 h-10 rounded-full bg-[#008dcc] flex items-center justify-center'>
                  <UserCheck className='w-5 h-5 text-white' />
                </div>
                <div>
                  <p className='font-bold text-sm text-foreground'>
                    Senior Engineers
                  </p>
                  <p className='text-[10px] text-muted-foreground'>
                    Plenty at the top
                  </p>
                </div>
              </div>
              <Check className='w-7 h-7 text-[#008dcc]' />
            </div>

            {/* AI tools level */}
            <div className='flex items-center gap-2 my-2'>
              <div className='flex-1 h-0.5 bg-[#FDC304]' />
              <div className='px-4 py-2 rounded-full bg-[#FDC304] text-black'>
                <span className='text-xs font-bold'>
                  AI TOOLS DOING GRUNT WORK
                </span>
              </div>
              <div className='flex-1 h-0.5 bg-[#FDC304]' />
            </div>

            {/* Junior level - hollowed out */}
            <div className='w-full p-4 rounded-xl bg-red-500/10 border-2 border-dashed border-red-500 flex items-center justify-between'>
              <div className='flex items-center gap-3'>
                <div className='w-10 h-10 rounded-full border-2 border-dashed border-red-500 flex items-center justify-center opacity-50'>
                  <User className='w-5 h-5 text-muted-foreground' />
                </div>
                <div>
                  <p className='font-bold text-sm text-foreground opacity-50'>
                    Junior Developers
                  </p>
                  <p className='text-[10px] text-red-500'>
                    Very few learning the craft
                  </p>
                </div>
              </div>
              <AlertTriangle className='w-7 h-7 text-red-500' />
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div className='grid grid-cols-3 gap-3 w-full max-w-lg'>
          <div className='p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-center'>
            <p className='text-xl md:text-2xl font-bold text-red-500'>54%</p>
            <p className='text-[10px] text-muted-foreground'>
              Leaders hiring fewer juniors
            </p>
          </div>
          <div className='p-3 rounded-lg bg-[#FDC304]/10 border border-[#FDC304]/30 text-center'>
            <p className='text-xl md:text-2xl font-bold text-[#FDC304]'>2yr</p>
            <p className='text-[10px] text-muted-foreground'>
              Experience minimum for startups
            </p>
          </div>
          <div className='p-3 rounded-lg bg-[#008dcc]/10 border border-[#008dcc]/30 text-center'>
            <p className='text-xl md:text-2xl font-bold text-[#008dcc]'>
              2026-27
            </p>
            <p className='text-[10px] text-muted-foreground'>
              When missing talent hits
            </p>
          </div>
        </div>

        {/* Quote */}
        <div className='w-full max-w-lg p-4 rounded-xl bg-muted border-l-4 border-[#008dcc]'>
          <p className='text-sm italic text-foreground mb-2'>
            "Replacing juniors with AI is one of the dumbest things you could
            do."
          </p>
          <p className='text-xs text-muted-foreground'>
            — Matt Garman, CEO of AWS
          </p>
        </div>

        {/* The irony */}
        <div className='w-full max-w-lg p-4 rounded-xl bg-[#F90B8A]/10 border border-[#F90B8A]/30'>
          <div className='flex items-start gap-3'>
            <div className='w-8 h-8 rounded-full bg-[#F90B8A] flex items-center justify-center flex-shrink-0'>
              <Info className='w-4 h-4 text-white' />
            </div>
            <div>
              <p className='text-sm font-bold text-foreground mb-1'>
                The Irony
              </p>
              <p className='text-xs text-muted-foreground'>
                AI-generated technical debt requires human judgment to fix —
                precisely the judgment juniors develop through years of making
                mistakes and learning. By eliminating junior positions,
                organizations lack future capacity to address today's debt.
              </p>
            </div>
          </div>
        </div>

        {/* Key insight */}
        <div className='mt-2 px-4 py-2 bg-red-500/10 rounded-lg border border-red-500/30'>
          <p className='text-xs md:text-sm text-center text-foreground'>
            <span className='font-semibold'>
              The engineers needed in 2026-27
            </span>{' '}
            — those with 2-4 years debugging experience — won't exist because
            they weren't hired.
          </p>
        </div>
      </div>
    </div>
  )
}

// 2026-2027 Timeline Crisis Diagram
export function CrisisTimelineDiagram() {
  const timeline = [
    {
      year: '2024',
      event: 'AI adoption accelerates',
      status: 'past',
      color: '#22c55e',
    },
    {
      year: '2025',
      event: 'Trust plummets, debt accumulates',
      status: 'current',
      color: '#FDC304',
    },
    {
      year: '2026',
      event: '"Year of Technical Debt"',
      status: 'warning',
      color: '#f97316',
    },
    {
      year: '2027',
      event: 'Crisis remediation',
      status: 'danger',
      color: '#ef4444',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          THE 2026-2027 INFLECTION POINT
        </h3>
        <p className='text-sm text-muted-foreground'>
          Industry leaders predict crisis levels of accumulated technical debt
        </p>
      </div>

      <div className='flex flex-col items-center gap-6'>
        {/* Timeline visualization */}
        <div className='relative w-full max-w-lg'>
          {/* Progress line */}
          <div className='absolute top-6 left-8 right-8 h-2 bg-gradient-to-r from-green-500 via-yellow-500 via-orange-500 to-red-500 rounded-full' />

          {/* Timeline nodes */}
          <div className='flex justify-between relative z-10'>
            {timeline.map((item, idx) => (
              <div key={idx} className='flex flex-col items-center'>
                <div
                  className='w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center border-4 border-background'
                  style={{ backgroundColor: item.color }}
                >
                  <span className='text-xs md:text-sm font-bold text-white'>
                    {item.year}
                  </span>
                </div>
                <div className='mt-3 text-center max-w-[80px]'>
                  <p className='text-[10px] md:text-xs text-foreground font-medium'>
                    {item.event}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Warning quotes */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-3 w-full max-w-lg'>
          <div className='p-3 rounded-lg bg-orange-500/10 border border-orange-500/30'>
            <p className='text-xs italic text-foreground mb-2'>
              "Companies will be writing big checks for consultants to come in
              and clean up the mess."
            </p>
            <p className='text-[10px] text-muted-foreground'>
              — Matt Watson, LinkedIn
            </p>
          </div>
          <div className='p-3 rounded-lg bg-red-500/10 border border-red-500/30'>
            <p className='text-xs italic text-foreground mb-2'>
              "2026 is the year of technical debt — we're producing tech debt on
              top of tech debt."
            </p>
            <p className='text-[10px] text-muted-foreground'>
              — Vicki Abraham, Salesforce Advisor
            </p>
          </div>
        </div>

        {/* Gartner prediction */}
        <div className='w-full max-w-lg p-4 rounded-xl bg-[#008dcc]/10 border border-[#008dcc]/30'>
          <div className='flex items-center gap-3 mb-2'>
            <div className='w-10 h-10 rounded bg-[#008dcc] flex items-center justify-center'>
              <span className='text-white font-bold text-xs'>G</span>
            </div>
            <span className='font-bold text-sm text-foreground'>
              Gartner Prediction
            </span>
          </div>
          <p className='text-xs text-muted-foreground'>
            By 2028,{' '}
            <span className='font-semibold text-foreground'>
              more than half of enterprises
            </span>{' '}
            that built custom LLMs will abandon initiatives due to costs,
            complexity, and technical debt. One-third of all business software
            will contain agent functions — a 33x increase in 4 years.
          </p>
        </div>

        {/* Key insight */}
        <div className='mt-2 px-4 py-2 bg-red-500/10 rounded-lg border border-red-500/30'>
          <p className='text-xs md:text-sm text-center text-foreground'>
            <span className='font-semibold'>
              The reckoning is not hypothetical.
            </span>{' '}
            It's measurable now in code quality metrics, security trends, and
            trust erosion trajectories.
          </p>
        </div>
      </div>
    </div>
  )
}

// Mitigation Strategy Framework
export function MitigationFrameworkDiagram() {
  const strategies = [
    {
      phase: 'Short-Term',
      timeframe: '0-6 months',
      color: '#22c55e',
      actions: [
        'Establish governance policies',
        'Implement CI/CD quality gates',
        'Audit existing AI code',
        'Track quality metrics',
      ],
    },
    {
      phase: 'Medium-Term',
      timeframe: '6-18 months',
      color: '#FDC304',
      actions: [
        'Develop hybrid workflows',
        'Invest in upskilling',
        'Deploy AI governance tools',
        'Refactor high-risk code',
      ],
    },
    {
      phase: 'Long-Term',
      timeframe: '18+ months',
      color: '#008dcc',
      actions: [
        'Architect for AI modularity',
        'Cross-functional governance',
        'Industry framework alignment',
        'Cultivate engineering judgment',
      ],
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          MITIGATION ROADMAP
        </h3>
        <p className='text-sm text-muted-foreground'>
          A phased approach to managing AI-generated technical debt
        </p>
      </div>

      <div className='flex flex-col items-center gap-6'>
        {/* Three phases */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4 w-full'>
          {strategies.map((strategy, idx) => (
            <div
              key={idx}
              className='p-4 rounded-xl border-2 bg-background'
              style={{ borderColor: strategy.color }}
            >
              <div className='flex items-center gap-2 mb-3'>
                <div
                  className='w-8 h-8 rounded-full flex items-center justify-center'
                  style={{ backgroundColor: strategy.color }}
                >
                  <span className='text-white font-bold text-xs'>
                    {idx + 1}
                  </span>
                </div>
                <div>
                  <p className='font-bold text-sm text-foreground'>
                    {strategy.phase}
                  </p>
                  <p className='text-[10px] text-muted-foreground'>
                    {strategy.timeframe}
                  </p>
                </div>
              </div>
              <ul className='space-y-2'>
                {strategy.actions.map((action, i) => (
                  <li key={i} className='flex items-start gap-2'>
                    <div
                      className='w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0'
                      style={{ backgroundColor: strategy.color }}
                    />
                    <span className='text-[10px] md:text-xs text-muted-foreground'>
                      {action}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Core principles */}
        <div className='w-full max-w-lg p-4 rounded-xl bg-[#8b5cf6]/10 border border-[#8b5cf6]/30'>
          <p className='text-sm font-bold text-foreground text-center mb-3'>
            The Balanced Approach
          </p>
          <div className='grid grid-cols-2 gap-3'>
            <div className='flex items-center gap-2'>
              <Bot className='w-5 h-5 text-[#8b5cf6]' />
              <span className='text-xs text-muted-foreground'>
                AI handles 70-90% of code generation
              </span>
            </div>
            <div className='flex items-center gap-2'>
              <UserCheck className='w-5 h-5 text-[#8b5cf6]' />
              <span className='text-xs text-muted-foreground'>
                Humans focus on architecture & security
              </span>
            </div>
            <div className='flex items-center gap-2'>
              <Lock className='w-5 h-5 text-[#8b5cf6]' />
              <span className='text-xs text-muted-foreground'>
                Security built into prompts
              </span>
            </div>
            <div className='flex items-center gap-2'>
              <BarChart3 className='w-5 h-5 text-[#8b5cf6]' />
              <span className='text-xs text-muted-foreground'>
                Quality metrics tracked continuously
              </span>
            </div>
          </div>
        </div>

        {/* Key insight */}
        <div className='mt-2 px-4 py-2 bg-emerald-500/10 rounded-lg border border-emerald-500/30'>
          <p className='text-xs md:text-sm text-center text-foreground'>
            <span className='font-semibold'>
              Don't avoid AI tools — govern them.
            </span>{' '}
            The companies that thrive will balance AI speed with human judgment
            and invest proactively in technical debt measurement.
          </p>
        </div>
      </div>
    </div>
  )
}
